import { PACKAGING_TYPES, GIFT_NOTE_TYPES } from 'constants';
import { getAdjustedPrice } from 'modules/clients/utils';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import { getPercentOff } from '../../products/selectors';

const xml2js = require('xml2js');

export const mapAddress = (address = {}) => ({
  ...(address.firstName && { firstName: address.firstName }),
  ...(address.lastName && { lastName: address.lastName }),
  line1: address.line1 || address.addressLine1 || address.address1,
  line2: address.line2 || address.addressLine2 || address.address2,
  city: address.city,
  country: address.country || address.countryCode || 'US',
  state: address.state || address.stateCode,
  zip: address.zip || address.postalCode || address.zipCode || '00000',
  default: address.defaultAddress || address.default,
  phone: address.dayTelephone || address.phone,
  contactAddressName: address.contactAddressName,
});

export const updateDefaultAddress = (arr = [], payload) => {
  const obj = { ...mapAddress(payload), id: arr.length.toString() };
  if (payload.defaultAddress) {
    const updatedArr = arr.map((item) => ({ ...item, default: false }));
    return [...updatedArr, obj];
  }
  return [...arr, obj];
};

const getPromoPrice = (originalPrice, percentOff) => {
  if (!originalPrice || !percentOff) return null;
  return (originalPrice * (100 - percentOff) * 0.01).toFixed(2);
};

export const mapProduct = (product = {}) => {
  const customizations = product.customizations || {};
  const percentOff = getPercentOff(product);
  const promoPrice = getPromoPrice(product.price?.currentPrice, percentOff);

  return {
    itemskuId: product.itemskuId,
    cmosskuId: product.cmosskuId,
    webProductSku: product.webProductSku,
    name: product.name,
    brand: product.brand,
    color: product.color,
    size: product.size || '',
    imageUrl: product.imageUrl,
    thumbnailUrl: product.thumbnailUrl,
    canonicalUrl: product.canonicalUrl,
    inventory: product.inventory || 0,
    quantity: product.quantity || 0,
    offerItemId: product.offerItemId,
    clientId: product.clientId,
    createdBy: product.createdBy || '',
    status: product.status || '',
    trackingNumber: product.trackingNumber || '',
    isCustomizable: product.isCustomizable,
    isPerishable: product.isPerishable,
    isIce: product.isIce,
    expectedShipDate: product.expectedShipDate,
    isBackorder: product.backorderFlag === 'Y' && product.inventory === 0,
    isDropship: product.dropshipFlag === 'Y',
    dropshipCategoryId: product.dropshipCategoryId || '',

    promotionsApplied: product.promotionsApplied || [],
    promotionDiscAmt: product.promotionDiscAmt,
    isGiftWrappable: product.giftWrappableFlag === 'Y',
    bopsAvailability: product.bopsAvailability,
    promotions: product.promotions,
    departmentCode: product.departmentCode,
    percentOff,

    price: {
      currentPrice: product.price?.currentPrice,
      currencyCode: product.price?.currencyCode,
      promoPrice,
      adjustedPromoPrice: getAdjustedPrice(promoPrice, customizations.adjustmentValue, customizations.priceType),
      itemPriceEach: product.price?.itemPriceEach || '',
      itemPriceExt: product.price?.itemPriceExt || '',
      itemTax: product.price?.itemTax || '',
      itemTaxEach: product.price?.itemTaxEach || '',
      itemDeliveryPrice: product.price?.itemDeliveryPrice || '',
      itemOtherPrice: product.price?.itemOtherPrice || '',
      itemDutyPrice: product.price?.itemDutyPrice || '',
      adjustedPrice: customizations.adjustedPrice || product.price?.adjustedPrice || '',
      priceOverrideReason: customizations.priceOverrideReason || product.price?.priceOverrideReason || '',
      priceOverrideReasonDetails:
        customizations.priceOverrideReasonDetails || product.price?.priceOverrideReasonDetails || '',
      orderPrice: product.price?.orderPrice || '',
    },
    giftOptions: {
      giftWrapCode: customizations.giftWrapCode || product.giftOptions?.giftWrapCode || PACKAGING_TYPES.NO_PACKAGING,
      giftCard: customizations.giftCard || product.giftOptions?.giftCard || GIFT_NOTE_TYPES.NO_GIFT_NOTE,
      giftMessage1: customizations.giftMessage1 || product.giftOptions?.giftMessage1 || '',
      giftMessage2: customizations.giftMessage2 || product.giftOptions?.giftMessage2 || '',
      giftMessage3: customizations.giftMessage3 || product.giftOptions?.giftMessage3 || '',
      giftMessage4: customizations.giftMessage4 || product.giftOptions?.giftMessage4 || '',
      giftMessage5: customizations.giftMessage5 || product.giftOptions?.giftMessage5 || '',
    },
    isLoading: false,
  };
};

const getNestedObject = (nestedObj, pathArr) => {
  return pathArr.reduce((obj, key) => {
    return obj && obj[key] !== 'undefined' ? obj[key] : undefined;
  }, nestedObj);
};

export const convertToXml = (address) => {
  const { line1, line2, city: cityName, state: stateName, zip: postalCode } = address;

  const builder = new xml2js.Builder({
    headless: true,
    explicitRoot: false,
    xmldec: false,
    rootName: 'soapenv:Envelope',
  });

  return builder.buildObject({
    $: {
      'xmlns:soapenv': 'http://schemas.xmlsoap.org/soap/envelope/',
      'xmlns:web': 'http://service.neimanmarcus.com/esb/web-services',
    },
    'soapenv:Body': {
      'web:addressStandardize': {
        addresses: {
          address: {
            inputAddress: {
              firmName: 'NEIMAN MARCUS',
              addressLine1: line1,
              addressLine2: line2,
              cityName,
              stateName,
              postalCode,
              foreignPostalCode: '',
              countryName: 'US',
              clientType: 'CMOS',
            },
          },
        },
      },
    },
  });
};

const parseXml = (xml) => {
  let parsed;
  const parser = xml2js.Parser({ explicitArray: false });
  parser.parseString(xml, (err, result) => {
    if (err) {
      throw err;
    } else {
      parsed = result;
    }
  });
  return parsed;
};

export const getStandardizeAddress = (xml) => {
  const result = parseXml(xml);
  return getNestedObject(result, ['NS1:Envelope', 'NS1:Body'])['nesb:addressStandardize'].addresses.address;
};

export const sortRTCDataCollection = (rtcDataCollection) => orderBy(rtcDataCollection, ['completed'], ['asc']);

export const getRTCUpdatedDataClone = (currentDataCollection, updatedItems, isSortedByCompleted) => {
  const dataClone = cloneDeep(currentDataCollection);
  const updatedCollection = updatedItems instanceof Array ? updatedItems : [updatedItems];

  updatedCollection.forEach(({ timestamp, completed }) => {
    const targetDataObject = dataClone.find((dataItem) => dataItem.timestamp === timestamp);
    if (targetDataObject) {
      targetDataObject.completed = completed;
    }
  });

  return isSortedByCompleted ? sortRTCDataCollection(dataClone) : dataClone;
};

export const splitPlccAndNoPlccKiNumbers = (loyaltyAssets, plccKiNumbers, nonPlccKiNumbers) =>
  Object.entries(loyaltyAssets).reduce(
    (acc, [key, value]) => {
      if (plccKiNumbers.includes(key)) {
        acc.loyaltyAssets[key] = value;
      }
      if (nonPlccKiNumbers.includes(key)) {
        acc.nonPlccAssets[key] = value;
      }

      return acc;
    },
    { loyaltyAssets: {}, nonPlccAssets: {} },
  );
